import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Check } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectOrganization,
  selectOrganizationError,
} from "../organization-slice";
import { renameOrganization } from "../slice-extensions/organization-slice-extension";

/**
 * Card for renaming the organization (Owner/Admin).
 */
const OrganizationRenameCard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const organization = useAppSelector(selectOrganization);
  const lastError = useAppSelector(selectOrganizationError);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (organization) {
      setName(organization.name);
    }
  }, [organization]);

  const isDirty = organization !== null && name.trim() !== organization.name;

  const handleSave = async () => {
    if (!organization || !isDirty || name.trim() === "") {
      return;
    }
    setSaving(true);
    setSaved(false);
    const result = await dispatch(
      renameOrganization({ organizationId: organization.id, name: name.trim() })
    );
    setSaving(false);
    if (renameOrganization.fulfilled.match(result)) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <Building2 size={20} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight">
            {t("organization.rename.title", "Organization name")}
          </h2>
          <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">
            {t("organization.rename.subtitle", "Shown to instructors you invite.")}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <input
          type="text"
          aria-label={t("organization.rename.title", "Organization name")}
          className="input w-full lg:max-w-md"
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
          disabled={organization === null || saving}
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!isDirty || name.trim() === "" || saving}
          >
            {t("common.save", "Save")}
          </button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-green-700 dark:text-green-400">
              <Check size={16} /> {t("organization.rename.saved", "Saved")}
            </span>
          )}
        </div>
      </div>
      {lastError && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">
          {lastError.message}
        </p>
      )}
    </div>
  );
};

export default OrganizationRenameCard;
